import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Loader2, Search, Check, X, RotateCcw, Merge, Split, Sparkles, Trash2, BarChart3, Link2Off, Link2, UserPlus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * PlayHQIdsPanel
 * ==============
 * Admin-only registry: every Derwent CC player found in synced PlayHQ
 * game summaries is listed here alongside an editable PlayHQ id field.
 *
 * - Data source is 100% internal (aggregated from ``playhq_game_summaries``
 *   filtered to Derwent teams — no live PlayHQ calls).
 * - Only the name + PlayHQ id are shown (per spec).
 * - Rows are sorted by appearance count so the most-active players
 *   surface first; a search bar filters by name.
 *
 * Backend contract:
 *   GET  /api/playhq/admin/players/registry
 *   PUT  /api/playhq/admin/players/registry   body {name_key, name, playhq_id}
 */
export default function PlayHQIdsPanel({ onDataChange }) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [drafts, setDrafts] = useState({});       // name_key -> id string being edited
  const [nameDrafts, setNameDrafts] = useState({}); // name_key -> name string being edited
  const [saving, setSaving] = useState({});       // name_key -> bool
  const [selected, setSelected] = useState(() => new Set());  // name_keys checked for merge
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeCanonical, setMergeCanonical] = useState("");
  const [merging, setMerging] = useState(false);
  // Duplicate scan
  const [dupOpen, setDupOpen] = useState(false);
  const [dupLoading, setDupLoading] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);      // scan result
  const [dupSelected, setDupSelected] = useState(new Set());  // canonical_name_keys chosen for bulk merge

  // Stat overrides
  const [overridesByKey, setOverridesByKey] = useState({}); // player_key -> {field: value}
  const [statsEditFor, setStatsEditFor] = useState(null);   // player row currently being edited
  const [statsDraft, setStatsDraft] = useState({});         // field -> string (draft values)
  const [statsSaving, setStatsSaving] = useState(false);
  const [dupMerging, setDupMerging] = useState(false);
  // Save-all
  const [savingAll, setSavingAll] = useState(false);
  const [creatingGuest, setCreatingGuest] = useState(false);
  // Link to member
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkMembers, setLinkMembers] = useState([]);      // approved members list
  const [linkMembersLoading, setLinkMembersLoading] = useState(false);
  const [linkFilter, setLinkFilter] = useState("");
  const [linkTarget, setLinkTarget] = useState("");         // selected member_user_id
  const [linking, setLinking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [regRes, ovRes] = await Promise.all([
        api.get("/playhq/admin/players/registry"),
        api.get("/playhq/admin/players/stats-overrides"),
      ]);
      setPlayers(regRes.data?.players || []);
      const map = {};
      for (const o of (ovRes.data?.overrides || [])) {
        if (o.player_key) map[o.player_key] = o.overrides || {};
      }
      setOverridesByKey(map);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load player list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  /**
   * Load the player's current aggregated stats + any saved override
   * values so admins see the live number in the input placeholder and
   * the current override (if any) prefilled. Called when the "Edit
   * stats" button on a row is tapped.
   */
  const openStatsEditor = async (p) => {
    setStatsEditFor(p);
    setStatsDraft({});
    try {
      // Grab the live aggregated row for this player from the leaderboards
      // endpoint (no filters = career totals).
      const { data } = await api.get("/playhq/leaderboards");
      const row = (data?.players || []).find((x) => x.player_key === p.name_key);
      const prefilled = {};
      const existing = overridesByKey[p.name_key] || {};
      const fields = [
        "matches", "innings_batted", "runs", "batting_average", "highest",
        "not_outs", "fifties", "centuries", "ducks",
        "overs", "wickets", "runs_conceded", "maidens",
        "bowling_average", "economy", "best_bowling",
        "catches", "wk_catches", "non_wk_catches", "stumpings", "run_outs",
      ];
      for (const f of fields) {
        // Prefill with override value if present, else empty so admins
        // see the live aggregate as placeholder text.
        prefilled[f] = existing[f] !== undefined && existing[f] !== null ? String(existing[f]) : "";
        prefilled[`__computed_${f}`] = row?.[f] != null ? String(row[f]) : "—";
      }
      setStatsDraft(prefilled);
    } catch (err) {
      toast.error("Couldn't load current stats");
      setStatsEditFor(null);
    }
  };

  const saveStatsOverride = async () => {
    if (!statsEditFor) return;
    setStatsSaving(true);
    try {
      // Strip the computed placeholder keys before shipping.
      const cleaned = {};
      Object.entries(statsDraft).forEach(([k, v]) => {
        if (k.startsWith("__computed_")) return;
        if (v === "" || v == null) return;
        cleaned[k] = v;
      });
      const res = await api.put("/playhq/admin/players/stats-overrides", {
        player_key: statsEditFor.name_key,
        player_name: statsEditFor.name,
        overrides: cleaned,
      });
      const saved = res.data?.overrides || {};
      setOverridesByKey((prev) => {
        const next = { ...prev };
        if (Object.keys(saved).length === 0) delete next[statsEditFor.name_key];
        else next[statsEditFor.name_key] = saved;
        return next;
      });
      toast.success(
        Object.keys(saved).length === 0
          ? `Override cleared · ${statsEditFor.name}`
          : `Override saved · ${statsEditFor.name}`
      );
      setStatsEditFor(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setStatsSaving(false);
    }
  };

  const clearStatsOverride = async () => {
    if (!statsEditFor) return;
    if (!window.confirm(`Clear override for ${statsEditFor.name}? Numbers revert to the aggregate.`)) return;
    setStatsSaving(true);
    try {
      await api.delete(`/playhq/admin/players/stats-overrides/${encodeURIComponent(statsEditFor.name_key)}`);
      setOverridesByKey((prev) => {
        const next = { ...prev };
        delete next[statsEditFor.name_key];
        return next;
      });
      toast.success(`Override cleared · ${statsEditFor.name}`);
      setStatsEditFor(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clear failed");
    } finally {
      setStatsSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return players;
    return players.filter((p) =>
      p.name_key.includes(term) ||
      (p.name || "").toLowerCase().includes(term) ||
      (p.source_name || "").toLowerCase().includes(term) ||
      (p.playhq_id || "").toLowerCase().includes(term)
    );
  }, [players, q]);

  const withIds = useMemo(() => players.filter((p) => (p.playhq_id || "").trim()).length, [players]);

  const setDraft = (key, val) => setDrafts((prev) => ({ ...prev, [key]: val }));
  const setNameDraft = (key, val) => setNameDrafts((prev) => ({ ...prev, [key]: val }));

  const draftFor = (row) => {
    if (drafts[row.name_key] !== undefined) return drafts[row.name_key];
    // No admin edit yet — show the saved id if present, else fall back
    // to the latest appearance id from the internal data so the input
    // always shows the player's current PlayHQ id.
    return row.playhq_id || row.default_id || "";
  };
  const nameFor = (row) => (nameDrafts[row.name_key] !== undefined ? nameDrafts[row.name_key] : row.name);

  // "Dirty" means the visible input value differs from what's persisted
  // as the admin override. This makes accepting a pre-populated default
  // a single Save click, and any manual edit likewise saveable.
  const isDirty = (row) => draftFor(row) !== (row.playhq_id || "") || nameFor(row) !== row.name;

  const save = async (row) => {
    const key = row.name_key;
    // Save the currently displayed values (either the admin's drafts or
    // the pre-populated defaults). Persisting the default effectively
    // "accepts" it as the confirmed value for this player.
    const idVal = (drafts[key] !== undefined ? drafts[key] : (row.playhq_id || row.default_id || "")).trim();
    const nameVal = (nameDrafts[key] !== undefined ? nameDrafts[key] : row.name).trim();
    // Only send a display_name override when it differs from PlayHQ's
    // raw source name — otherwise the backend clears the override.
    const sourceName = row.source_name || row.name;
    const displayName = nameVal && nameVal !== sourceName ? nameVal : "";
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.put("/playhq/admin/players/registry", {
        name_key: key,
        name: sourceName,
        playhq_id: idVal,
        display_name: displayName,
      });
      // Reflect in local state so appearance_count is preserved without a full refetch.
      setPlayers((prev) => prev.map((p) => p.name_key === key ? {
        ...p,
        playhq_id: idVal,
        source_name: sourceName,
        name: displayName || sourceName,
        display_name: displayName,
      } : p));
      setDrafts((prev) => { const cp = { ...prev }; delete cp[key]; return cp; });
      setNameDrafts((prev) => { const cp = { ...prev }; delete cp[key]; return cp; });
      toast.success(idVal || displayName ? `Saved · ${nameVal || sourceName}` : `Cleared · ${sourceName}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving((s) => { const cp = { ...s }; delete cp[key]; return cp; });
    }
  };

  const cancel = (row) => {
    setDrafts((prev) => { const cp = { ...prev }; delete cp[row.name_key]; return cp; });
    setNameDrafts((prev) => { const cp = { ...prev }; delete cp[row.name_key]; return cp; });
  };

  // ---------------- Save all ----------------
  // A row is "pending save" when its visible values (either explicit
  // drafts or the pre-populated default id) differ from what's stored.
  // We only iterate the currently *filtered* list so admins can scope
  // a bulk save via the search bar if they want.
  const pendingRows = useMemo(() => filtered.filter((p) => isDirty(p)), [filtered, drafts, nameDrafts]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveAll = async () => {
    if (pendingRows.length === 0) return;
    setSavingAll(true);
    let done = 0;
    try {
      // Fan out one PUT per row. Locally reflect each result so counts
      // stay accurate without a full refetch at the end.
      for (const row of pendingRows) {
        const key = row.name_key;
        const idVal = (drafts[key] !== undefined ? drafts[key] : (row.playhq_id || row.default_id || "")).trim();
        const nameVal = (nameDrafts[key] !== undefined ? nameDrafts[key] : row.name).trim();
        const sourceName = row.source_name || row.name;
        const displayName = nameVal && nameVal !== sourceName ? nameVal : "";
        // eslint-disable-next-line no-await-in-loop
        await api.put("/playhq/admin/players/registry", {
          name_key: key,
          name: sourceName,
          playhq_id: idVal,
          display_name: displayName,
        });
        setPlayers((prev) => prev.map((p) => p.name_key === key ? {
          ...p,
          playhq_id: idVal,
          source_name: sourceName,
          name: displayName || sourceName,
          display_name: displayName,
        } : p));
        done += 1;
      }
      setDrafts({});
      setNameDrafts({});
      toast.success(`Saved ${done} row${done === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save all failed");
    } finally {
      setSavingAll(false);
    }
  };

  const clear = async (row) => {
    // Clear both the id and the name override for this row via a direct
    // API call so we don't race React state updates.
    const key = row.name_key;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.put("/playhq/admin/players/registry", {
        name_key: key,
        name: row.source_name || row.name,
        playhq_id: "",
        display_name: "",
      });
      setPlayers((prev) => prev.map((p) => p.name_key === key ? {
        ...p,
        playhq_id: "",
        name: p.source_name || p.name,
        display_name: "",
      } : p));
      setDrafts((prev) => { const cp = { ...prev }; delete cp[key]; return cp; });
      setNameDrafts((prev) => { const cp = { ...prev }; delete cp[key]; return cp; });
      toast.success(`Cleared · ${row.source_name || row.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clear failed");
    } finally {
      setSaving((s) => { const cp = { ...s }; delete cp[key]; return cp; });
    }
  };

  /**
   * Hard-delete the registry document for a player: wipes both the
   * PlayHQ ID override and the admin-chosen display name in one round
   * trip via `DELETE /admin/players/registry/{name_key}`. Match stats
   * are untouched — the player still shows up on leaderboards, they
   * simply lose the admin overlay and revert to PlayHQ's raw values.
   */
  const deleteRegistry = async (row) => {
    const key = row.name_key;
    const label = row.source_name || row.name || key;
    if (typeof window !== "undefined" && !window.confirm(
      `Delete "${label}" from the PlayHQ registry?\n\n` +
      `They'll disappear from this list. Their match stats stay intact ` +
      `on the leaderboards; saving a fresh PlayHQ ID or display name for ` +
      `this player later will bring them back.`
    )) {
      return;
    }
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.delete(`/playhq/admin/players/registry/${encodeURIComponent(key)}`);
      // Backend marks the row as hidden — mirror that locally by
      // removing it from the list so the panel updates instantly.
      setPlayers((prev) => prev.filter((p) => p.name_key !== key));
      setSelected((prev) => { const next = new Set(prev); next.delete(key); return next; });
      setDrafts((prev) => { const cp = { ...prev }; delete cp[key]; return cp; });
      setNameDrafts((prev) => { const cp = { ...prev }; delete cp[key]; return cp; });
      toast.success(`Deleted · ${label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setSaving((s) => { const cp = { ...s }; delete cp[key]; return cp; });
    }
  };

  const unlinkMember = async (row) => {
    const memberName = `${row.member_first_name || ""} ${row.member_last_name || ""}`.trim();
    const label = row.name || row.name_key;
    if (typeof window !== "undefined" && !window.confirm(
      `Unlink ${memberName || "the linked member"} from "${label}"?\n\n` +
      `Their profile's PlayHQ id will be cleared and they'll re-appear in the ` +
      `"unlinked" pool. Match stats on the leaderboard are untouched.`
    )) {
      return;
    }
    const key = row.name_key;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.post(`/playhq/admin/players/registry/${encodeURIComponent(key)}/unlink`);
      // Local optimistic: drop the member_* fields so the chip + button
      // disappear immediately.
      setPlayers((prev) => prev.map((p) => p.name_key === key ? {
        ...p,
        member_user_id: undefined,
        member_id: undefined,
        member_first_name: undefined,
        member_last_name: undefined,
      } : p));
      toast.success(`Unlinked${memberName ? ` · ${memberName}` : ""}`);
      onDataChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unlink failed");
    } finally {
      setSaving((s) => { const cp = { ...s }; delete cp[key]; return cp; });
    }
  };

  // ---------------- Link to member ----------------
  const openLinkDialog = async () => {
    if (selected.size !== 1) return;
    const row = players.find((p) => selected.has(p.name_key));
    if (!row || row.member_user_id) {
      toast.error("This player is already linked to a member");
      return;
    }
    setLinkOpen(true);
    setLinkFilter("");
    setLinkTarget("");
    setLinkMembersLoading(true);
    try {
      const { data } = await api.get("/members/all");
      // Filter to approved members only
      const approved = (data || []).filter((m) => m.status === "approved" && !m.is_guest_player);
      setLinkMembers(approved);
    } catch {
      toast.error("Failed to load members");
      setLinkOpen(false);
    } finally {
      setLinkMembersLoading(false);
    }
  };

  const createGuestFromSelected = async () => {
    if (selected.size !== 1) return;
    const row = players.find((p) => selected.has(p.name_key));
    if (!row || row.member_user_id) {
      toast.error("Already linked to a member");
      return;
    }
    setCreatingGuest(true);
    try {
      await api.post(`/playhq/admin/player-ids/${encodeURIComponent(row.name_key)}/create-guest`);
      toast.success(`${row.name} added as guest member`);
      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create guest");
    } finally {
      setCreatingGuest(false);
    }
  };


  const confirmLink = async () => {
    if (!linkTarget || selected.size !== 1) return;
    const row = players.find((p) => selected.has(p.name_key));
    if (!row) return;
    setLinking(true);
    try {
      const { data } = await api.post(
        `/playhq/admin/players/registry/${encodeURIComponent(row.name_key)}/link`,
        { member_user_id: linkTarget }
      );
      // Optimistic update
      setPlayers((prev) => prev.map((p) =>
        p.name_key === row.name_key ? {
          ...p,
          member_user_id: data.member_user_id,
          member_first_name: data.member_first_name,
          member_last_name: data.member_last_name,
          member_id: data.member_id,
        } : p
      ));
      toast.success(`Linked · ${data.member_first_name || ""} ${data.member_last_name || ""}`.trim());
      onDataChange?.();
      setLinkOpen(false);
      setSelected(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link failed");
    } finally {
      setLinking(false);
    }
  };

  const filteredLinkMembers = useMemo(() => {
    const term = linkFilter.trim().toLowerCase();
    if (!term) return linkMembers;
    return linkMembers.filter((m) =>
      `${m.first_name || ""} ${m.last_name || ""}`.toLowerCase().includes(term) ||
      (m.member_id || "").toLowerCase().includes(term)
    );
  }, [linkMembers, linkFilter]);

  // ---------------- Selection + merge ----------------
  const toggleSelect = (name_key) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(name_key)) next.delete(name_key); else next.add(name_key);
    return next;
  });
  const selectedRows = useMemo(
    () => players.filter((p) => selected.has(p.name_key)),
    [players, selected]
  );
  const clearSelection = () => setSelected(new Set());

  const openMergeDialog = () => {
    if (selected.size < 2) {
      toast.error("Pick at least two rows to merge");
      return;
    }
    // Default canonical = the row with the highest appearance count.
    const top = [...selectedRows].sort((a, b) => (b.appearance_count || 0) - (a.appearance_count || 0))[0];
    setMergeCanonical(top?.name_key || "");
    setMergeOpen(true);
  };

  const performMerge = async () => {
    if (!mergeCanonical) return;
    const aliases = [...selected].filter((k) => k !== mergeCanonical);
    if (aliases.length === 0) return;
    setMerging(true);
    try {
      await api.post("/playhq/admin/players/merge", {
        canonical: mergeCanonical,
        aliases,
      });
      toast.success(`Merged ${aliases.length} row${aliases.length === 1 ? "" : "s"} into canonical`);
      setMergeOpen(false);
      clearSelection();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  const unmerge = async (row) => {
    const aliases = (row.merged_from || []).map((m) => m.name_key);
    if (aliases.length === 0) return;
    try {
      await api.post("/playhq/admin/players/unmerge", { aliases });
      toast.success(`Split ${aliases.length} alias${aliases.length === 1 ? "" : "es"} out of ${row.name}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Split failed");
    }
  };

  // ---------------- Duplicate scan ----------------
  const openDupDialog = async () => {
    setDupOpen(true);
    setDupLoading(true);
    setDupGroups([]);
    setDupSelected(new Set());
    try {
      const { data } = await api.get("/playhq/admin/players/duplicates");
      const groups = data?.groups || [];
      setDupGroups(groups);
      // Pre-select every group so "Merge all" is one tap; admin can
      // uncheck any group they want to review manually.
      setDupSelected(new Set(groups.map((g) => g.canonical_name_key)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed");
    } finally {
      setDupLoading(false);
    }
  };

  const toggleDupGroup = (key) => setDupSelected((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const mergeSelectedDuplicateGroups = async () => {
    if (dupSelected.size === 0) return;
    setDupMerging(true);
    let done = 0;
    try {
      for (const g of dupGroups) {
        if (!dupSelected.has(g.canonical_name_key)) continue;
        const aliases = g.members
          .map((m) => m.name_key)
          .filter((k) => k !== g.canonical_name_key);
        if (aliases.length === 0) continue;
        // Backend accepts one canonical + its aliases per call, so we
        // fan out per group. 228 groups round-trips fast enough; if it
        // ever becomes an issue we can add a batch endpoint.
        // eslint-disable-next-line no-await-in-loop
        await api.post("/playhq/admin/players/merge", {
          canonical: g.canonical_name_key,
          aliases,
        });
        done += 1;
      }
      toast.success(`Merged ${done} duplicate group${done === 1 ? "" : "s"}`);
      setDupOpen(false);
      setDupGroups([]);
      setDupSelected(new Set());
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk merge failed");
    } finally {
      setDupMerging(false);
    }
  };

  return (
    <Card className="mt-4 p-4 sm:p-5 rounded-2xl" data-testid="playhq-ids-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
            PlayHQ player registry
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-lg">
            Every Derwent CC player from the internal PlayHQ dataset. Edit and save each player's PlayHQ id — the mapping is used to link scorecards, leaderboards and stats.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <>
              <span className="text-[11px] text-muted-foreground tabular-nums" data-testid="playhq-ids-selected-count">
                {selected.size} selected
              </span>
              <Button size="sm" variant="ghost" onClick={clearSelection} className="h-8 gap-1 text-muted-foreground" data-testid="playhq-ids-clear-selection">
                <X className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" onClick={openMergeDialog} disabled={selected.size < 2} className="h-8 gap-1.5" data-testid="playhq-ids-merge-button">
                <Merge className="h-3.5 w-3.5" />
                Merge selected
              </Button>
              {selected.size === 1 && (() => {
                const selRow = players.find((p) => selected.has(p.name_key));
                return selRow && !selRow.member_user_id ? (
                  <>
                    <Button size="sm" variant="outline" onClick={openLinkDialog} className="h-8 gap-1.5" data-testid="playhq-ids-link-member-button">
                      <Link2 className="h-3.5 w-3.5" />
                      Link to Member
                    </Button>
                    <Button size="sm" variant="outline" onClick={createGuestFromSelected} disabled={creatingGuest} className="h-8 gap-1.5" data-testid="playhq-ids-add-guest-button">
                      {creatingGuest ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
                      Add as Guest
                    </Button>
                  </>
                ) : null;
              })()}
            </>
          )}
          <Badge variant="secondary" className="tabular-nums text-[11px]">
            {withIds} / {players.length} linked
          </Badge>
          {pendingRows.length > 0 && (
            <Button size="sm" onClick={saveAll} disabled={savingAll} className="h-8 gap-1.5" data-testid="playhq-ids-save-all">
              {savingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Save all ({pendingRows.length})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={openDupDialog} className="h-8 gap-1.5" data-testid="playhq-ids-find-duplicates">
            <Sparkles className="h-3.5 w-3.5" />
            Find duplicates
          </Button>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="h-8" data-testid="playhq-ids-refresh">
            <RotateCcw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by player name or PlayHQ id…"
          className="pl-9 h-10 rounded-lg"
          data-testid="playhq-ids-filter"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-10 flex items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading players…
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground italic">
          {q.trim() ? `No players match "${q.trim()}".` : "No Derwent players found in the internal dataset yet."}
        </div>
      ) : (
        <div className="mobile-scroll-x rounded-lg border border-border">
          <table className="w-full text-sm" data-testid="playhq-ids-table">
            <thead className="bg-[hsl(var(--secondary))] text-[10px] uppercase tracking-[0.14em] text-muted-foreground sticky top-0">
              <tr>
                <th className="text-left py-2.5 px-3 font-medium w-[42px]"></th>
                <th className="text-left py-2.5 px-3 font-medium">Player name</th>
                <th className="text-left py-2.5 px-3 font-medium">PlayHQ ID</th>
                <th className="text-right py-2.5 px-3 font-medium w-[140px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const dirty = isDirty(p);
                const val = draftFor(p);
                const savingRow = !!saving[p.name_key];
                const isSelected = selected.has(p.name_key);
                const mergedFrom = p.merged_from || [];
                return (
                  <tr
                    key={p.name_key}
                    className={`border-t border-border/60 hover:bg-[hsl(var(--secondary))]/40 ${isSelected ? "bg-[hsl(var(--accent))]/8" : ""}`}
                    data-testid={`playhq-ids-row-${p.name_key.replace(/\s+/g, "-")}`}
                  >
                    <td className="py-2 px-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(p.name_key)}
                        aria-label={`Select ${p.name}`}
                        data-testid={`playhq-ids-select-${p.name_key.replace(/\s+/g, "-")}`}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <Input
                        value={nameFor(p)}
                        onChange={(e) => setNameDraft(p.name_key, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && dirty) save(p); if (e.key === "Escape") cancel(p); }}
                        placeholder="Player name"
                        className={`h-9 rounded-md text-sm font-medium ${nameFor(p) !== p.name ? "border-[hsl(var(--accent))]" : ""}`}
                        data-testid={`playhq-ids-name-input-${p.name_key.replace(/\s+/g, "-")}`}
                        title={p.source_name && p.source_name !== p.name ? `PlayHQ source name: ${p.source_name}` : undefined}
                      />
                      {mergedFrom.length > 0 && (
                        <div className="mt-1 flex items-center gap-1 flex-wrap" data-testid={`playhq-ids-merged-badges-${p.name_key.replace(/\s+/g, "-")}`}>
                          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Merged from:</span>
                          {mergedFrom.map((m) => (
                            <Badge key={m.name_key} variant="outline" className="text-[10px] tabular-nums">
                              {m.name} · {m.appearance_count}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {p.member_user_id && (
                        <div className="mt-1 flex items-center gap-1.5" data-testid={`playhq-ids-linked-${p.name_key.replace(/\s+/g, "-")}`}>
                          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Linked to:</span>
                          <Badge variant="outline" className="text-[10px] tabular-nums border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))]">
                            {`${p.member_first_name || ""} ${p.member_last_name || ""}`.trim() || "Member"}
                            {p.member_id ? ` · ${p.member_id}` : ""}
                          </Badge>
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <Input
                        value={val}
                        onChange={(e) => setDraft(p.name_key, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && dirty) save(p); if (e.key === "Escape") cancel(p); }}
                        placeholder="Paste a PlayHQ player id…"
                        className={`h-9 rounded-md font-mono text-xs ${dirty ? "border-[hsl(var(--accent))]" : ""}`}
                        data-testid={`playhq-ids-input-${p.name_key.replace(/\s+/g, "-")}`}
                      />
                    </td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {mergedFrom.length > 0 && !dirty && (
                          <Button size="sm" variant="ghost" className="h-8 gap-1 text-muted-foreground hover:text-foreground" onClick={() => unmerge(p)} disabled={savingRow} title="Split merged aliases back out" data-testid={`playhq-ids-split-${p.name_key.replace(/\s+/g, "-")}`}>
                            <Split className="h-3.5 w-3.5" /> Split
                          </Button>
                        )}
                      {dirty ? (
                        <>
                          <Button size="sm" className="h-8 gap-1" onClick={() => save(p)} disabled={savingRow} data-testid={`playhq-ids-save-${p.name_key.replace(/\s+/g, "-")}`}>
                            {savingRow ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            Save
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 gap-1" onClick={() => cancel(p)} disabled={savingRow} data-testid={`playhq-ids-cancel-${p.name_key.replace(/\s+/g, "-")}`}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      ) : (
                        <>
                          {!(p.playhq_id || p.display_name) && mergedFrom.length === 0 && (
                            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mr-1" title="No admin override — showing the latest appearance id from PlayHQ data">
                              From data
                            </span>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className={`h-8 gap-1 text-muted-foreground ${overridesByKey[p.name_key] ? "text-[hsl(var(--accent))] hover:text-[hsl(var(--accent))]" : "hover:text-foreground"}`}
                            onClick={() => openStatsEditor(p)}
                            disabled={savingRow}
                            title={overridesByKey[p.name_key] ? "Edit stats override (active)" : "Edit stats override"}
                            data-testid={`playhq-ids-edit-stats-${p.name_key.replace(/\s+/g, "-")}`}
                          >
                            <BarChart3 className="h-3.5 w-3.5" />
                            Edit stats
                          </Button>
                          {p.member_user_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 gap-1 text-muted-foreground hover:text-amber-600 hover:bg-amber-500/10"
                              onClick={() => unlinkMember(p)}
                              disabled={savingRow}
                              title="Unlink the member from this PlayHQ id (recover from a wrong link)"
                              data-testid={`playhq-ids-unlink-${p.name_key.replace(/\s+/g, "-")}`}
                            >
                              {savingRow ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2Off className="h-3.5 w-3.5" />}
                              Unlink
                            </Button>
                          )}
                        </>
                      )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ============ Merge dialog ============ */}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent data-testid="playhq-ids-merge-dialog">
          <DialogHeader>
            <DialogTitle>Merge selected players</DialogTitle>
            <DialogDescription>
              Pick which spelling should become the canonical player. The others will be linked as aliases — their appearances and stats will roll up into the canonical row across scorecards, leaderboards, and the career lookup.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[320px] overflow-auto -mx-2 px-2">
            <RadioGroup value={mergeCanonical} onValueChange={setMergeCanonical} className="space-y-1.5">
              {selectedRows.map((r) => (
                <label
                  key={r.name_key}
                  htmlFor={`merge-radio-${r.name_key}`}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                    mergeCanonical === r.name_key
                      ? "border-[hsl(var(--accent))]/60 bg-[hsl(var(--accent))]/8"
                      : "border-border hover:border-foreground/25"
                  }`}
                  data-testid={`playhq-ids-merge-option-${r.name_key.replace(/\s+/g, "-")}`}
                >
                  <RadioGroupItem value={r.name_key} id={`merge-radio-${r.name_key}`} />
                  <span className="flex-1 truncate text-sm font-medium">{r.name}</span>
                  <Badge variant="secondary" className="tabular-nums text-[10px]">{r.appearance_count} apps</Badge>
                </label>
              ))}
            </RadioGroup>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMergeOpen(false)} disabled={merging} data-testid="playhq-ids-merge-cancel">Cancel</Button>
            <Button onClick={performMerge} disabled={merging || !mergeCanonical} className="gap-1.5" data-testid="playhq-ids-merge-confirm">
              {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Merge className="h-4 w-4" />}
              Merge into {selectedRows.find((r) => r.name_key === mergeCanonical)?.name || "canonical"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Duplicate-scan dialog ============ */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="playhq-ids-duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[hsl(var(--accent))]" />
              Likely duplicate players
            </DialogTitle>
            <DialogDescription>
              Rows below share a surname and have first-names that look like the same person (initial vs full name, all-initials, or shared prefix). Every group is pre-selected — un-tick any you want to review manually, then merge in one tap.
            </DialogDescription>
          </DialogHeader>

          {dupLoading ? (
            <div className="py-10 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Scanning…
            </div>
          ) : dupGroups.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground italic">
              No obvious duplicates found — every player looks distinct.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {dupGroups.length} group{dupGroups.length === 1 ? "" : "s"} detected · {dupSelected.size} selected
                </span>
                <div className="inline-flex gap-2">
                  <button
                    type="button"
                    onClick={() => setDupSelected(new Set(dupGroups.map((g) => g.canonical_name_key)))}
                    className="text-[10px] uppercase tracking-[0.14em] hover:text-foreground"
                    data-testid="playhq-ids-duplicates-select-all"
                  >
                    Select all
                  </button>
                  <span>·</span>
                  <button
                    type="button"
                    onClick={() => setDupSelected(new Set())}
                    className="text-[10px] uppercase tracking-[0.14em] hover:text-foreground"
                    data-testid="playhq-ids-duplicates-clear"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="max-h-[52vh] overflow-auto -mx-2 px-2 space-y-2">
                {dupGroups.map((g) => {
                  const isChecked = dupSelected.has(g.canonical_name_key);
                  const totalApps = g.members.reduce((s, m) => s + (m.appearance_count || 0), 0);
                  return (
                    <label
                      key={g.canonical_name_key}
                      className={`block rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                        isChecked
                          ? "border-[hsl(var(--accent))]/60 bg-[hsl(var(--accent))]/8"
                          : "border-border hover:border-foreground/25"
                      }`}
                      data-testid={`playhq-ids-duplicates-group-${g.canonical_name_key.replace(/\s+/g, "-")}`}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleDupGroup(g.canonical_name_key)}
                          className="mt-0.5"
                          data-testid={`playhq-ids-duplicates-check-${g.canonical_name_key.replace(/\s+/g, "-")}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-semibold truncate">{g.canonical_name}</span>
                            <Badge variant="secondary" className="tabular-nums text-[10px]">{totalApps} apps combined</Badge>
                          </div>
                          <div className="text-[11px] text-muted-foreground italic mb-1.5">{g.reason}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {g.members.map((m) => (
                              <Badge
                                key={m.name_key}
                                variant={m.name_key === g.canonical_name_key ? "default" : "outline"}
                                className="tabular-nums text-[10px] font-normal"
                              >
                                {m.name_key === g.canonical_name_key ? "★ " : ""}{m.name} · {m.appearance_count}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDupOpen(false)} disabled={dupMerging} data-testid="playhq-ids-duplicates-cancel">Cancel</Button>
            <Button
              onClick={mergeSelectedDuplicateGroups}
              disabled={dupMerging || dupSelected.size === 0}
              className="gap-1.5"
              data-testid="playhq-ids-duplicates-merge-all"
            >
              {dupMerging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Merge className="h-4 w-4" />}
              Merge {dupSelected.size} group{dupSelected.size === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Stats override dialog ============ */}
      <Dialog open={!!statsEditFor} onOpenChange={(v) => !v && setStatsEditFor(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="playhq-ids-stats-dialog">
          <DialogHeader>
            <DialogTitle>Edit stats · {statsEditFor?.name}</DialogTitle>
            <DialogDescription>
              Any value you type here overrides the aggregate on the career (all-seasons) leaderboard.
              Leave a field blank to keep the raw calculated number. Season / team filters ignore overrides.
            </DialogDescription>
          </DialogHeader>
          {statsEditFor && (
            <div className="max-h-[60vh] overflow-y-auto pr-1">
              <StatsEditGroup title="Batting" fields={[
                ["matches", "Matches"],
                ["innings_batted", "Innings"],
                ["runs", "Runs"],
                ["highest", "Highest score"],
                ["not_outs", "Not outs"],
                ["batting_average", "Average"],
                ["fifties", "50s"],
                ["centuries", "100s"],
                ["ducks", "Ducks"],
              ]} draft={statsDraft} setDraft={setStatsDraft} />

              <StatsEditGroup title="Bowling" fields={[
                ["overs", "Overs (e.g. 42.3)"],
                ["maidens", "Maidens"],
                ["runs_conceded", "Runs conceded"],
                ["wickets", "Wickets"],
                ["bowling_average", "Average"],
                ["economy", "Economy"],
                ["best_bowling", "Best bowling (e.g. 5/12)"],
              ]} draft={statsDraft} setDraft={setStatsDraft} />

              <StatsEditGroup title="Fielding" fields={[
                ["catches", "Catches (total)"],
                ["wk_catches", "Wk catches"],
                ["non_wk_catches", "Non-wk catches"],
                ["stumpings", "Stumpings"],
                ["run_outs", "Run outs"],
              ]} draft={statsDraft} setDraft={setStatsDraft} />
            </div>
          )}
          <DialogFooter className="gap-2">
            {overridesByKey[statsEditFor?.name_key] && (
              <Button
                variant="ghost"
                className="text-red-600 hover:text-red-700 hover:bg-red-500/10 mr-auto"
                onClick={clearStatsOverride}
                disabled={statsSaving}
                data-testid="playhq-ids-stats-clear"
              >
                Clear override
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setStatsEditFor(null)}
              disabled={statsSaving}
              data-testid="playhq-ids-stats-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={saveStatsOverride}
              disabled={statsSaving}
              data-testid="playhq-ids-stats-save"
            >
              {statsSaving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : "Save override"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Link to member dialog ============ */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-md" data-testid="playhq-ids-link-dialog">
          <DialogHeader>
            <DialogTitle>Link to Derwent member</DialogTitle>
            <DialogDescription>
              {(() => {
                const selRow = players.find((p) => selected.has(p.name_key));
                return selRow ? `Select the club member to link to "${selRow.name}".` : "Select a club member.";
              })()}
            </DialogDescription>
          </DialogHeader>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              value={linkFilter}
              onChange={(e) => setLinkFilter(e.target.value)}
              placeholder="Search members…"
              className="pl-9 h-9 rounded-lg"
              data-testid="playhq-ids-link-filter"
            />
          </div>
          <div className="max-h-[300px] overflow-y-auto border border-border rounded-lg">
            {linkMembersLoading ? (
              <div className="py-8 flex items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading members…
              </div>
            ) : filteredLinkMembers.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground italic">
                {linkFilter.trim() ? "No members match." : "No approved members found."}
              </div>
            ) : (
              <RadioGroup value={linkTarget} onValueChange={setLinkTarget} className="p-1">
                {filteredLinkMembers.map((m) => (
                  <label
                    key={m.id}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${linkTarget === m.id ? "bg-[hsl(var(--accent))]/10" : "hover:bg-[hsl(var(--secondary))]/60"}`}
                    data-testid={`playhq-ids-link-member-${m.id}`}
                  >
                    <RadioGroupItem value={m.id} id={`link-member-${m.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{m.first_name} {m.last_name}</div>
                      {m.member_id && <div className="text-[10px] text-muted-foreground">{m.member_id}</div>}
                    </div>
                    {m.playhq_id && (
                      <Badge variant="outline" className="text-[9px] shrink-0">Already linked</Badge>
                    )}
                  </label>
                ))}
              </RadioGroup>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)} disabled={linking} data-testid="playhq-ids-link-cancel">
              Cancel
            </Button>
            <Button onClick={confirmLink} disabled={!linkTarget || linking} data-testid="playhq-ids-link-confirm">
              {linking ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Linking…</> : <><Link2 className="h-4 w-4 mr-1.5" /> Link</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Card>
  );
}


// ---------------------------------------------------------------------------
// Small helper: one section of the stats-override dialog. Each field shows
// its current aggregate as placeholder text so admins can see the raw
// number they're overriding without having to leave the modal.
// ---------------------------------------------------------------------------
function StatsEditGroup({ title, fields, draft, setDraft }) {
  return (
    <div className="mb-5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-2">
        {title}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {fields.map(([key, label]) => {
          const value = draft[key] ?? "";
          const computed = draft[`__computed_${key}`] ?? "—";
          return (
            <div key={key}>
              <Label className="text-xs mb-1 block" htmlFor={`stats-${key}`}>
                {label}
                <span className="ml-2 text-muted-foreground/70">(aggregate: {computed})</span>
              </Label>
              <Input
                id={`stats-${key}`}
                value={value}
                onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder={computed !== "—" ? computed : ""}
                className="h-9 rounded-lg text-sm"
                data-testid={`stats-input-${key}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

